import {useCallback, useState} from 'react';

import {exportEntitlements} from 'src/openapi/admin/apis/EntitlementExportApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {Relation} from 'src/openapi/admin/models/Relation';

export const useExportEntitlements = () => {
    const apiDispatch = useAppDispatch();

    const [exportResult, setExportResult] = useState<EntitlementTransferContext | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const exportRelations = useCallback(async (relations: Relation[]) => {
        if (!relations.length || isExporting) {
            return;
        }

        setExportError(null);
        setIsExporting(true);

        try {
            const action = await apiDispatch(exportEntitlements({relation: relations}));

            if (action.payload.success) {
                setExportResult(action.payload.data);
                return {success: true, data: action.payload.data};
            } else {
                const errorMessage = action.payload.data
                    ? getError({error: action.payload.data})
                    : 'Failed to export entitlements.';
                setExportError(errorMessage);
                setExportResult(null);
                return {success: false, error: errorMessage};
            }
        } catch {
            const errorMessage = 'Failed to export entitlements.';
            setExportError(errorMessage);
            setExportResult(null);
            return {success: false, error: errorMessage};
        } finally {
            setIsExporting(false);
        }
    }, [apiDispatch, isExporting]);

    const clearExportResult = useCallback(() => {
        setExportResult(null);
    }, []);

    const clearExportError = useCallback(() => {
        setExportError(null);
    }, []);

    return {
        exportRelations,
        exportResult,
        isExporting,
        exportError,
        clearExportResult,
        clearExportError,
    };
};
